import { connectivityService } from '~/data/connectivity/ConnectivityService';
import {
  appReferenceRepository,
  documentationRepository,
  issueRepository,
  serviceRepository,
} from '~/data/repositories';
import store from '~/store';

export type AppDataLoadOptions = {
  /**
   * When true (session startup), wait for favorites/notification background fetches
   * before resolving — keeps the Cordova DB splash until cache is warm.
   */
  waitForBackground?: boolean;
};

/**
 * Boots reference data and offline cache after auth + SQLite are ready.
 * UserProvider must finish first (permissions online, or skip when offline).
 */
export class AppDataLoader {
  async load(options: AppDataLoadOptions = {}): Promise<void> {
    const { waitForBackground = false } = options;
    const actions = store.getActions();

    if (connectivityService.isOnline()) {
      await this.loadReferenceDataFromRepositories();
      const backgroundJob = this.runOnlineBackgroundSync(actions);
      if (waitForBackground) {
        await backgroundJob;
      }
      return;
    }

    actions.notification.resetNotifications();
    actions.chat.resetNotifications();
    await this.loadReferenceDataFromRepositories();
  }

  /** Reference tables only (users, departments, issue metadata). */
  private async loadReferenceDataFromRepositories(): Promise<void> {
    const actions = store.getActions();

    const users = await appReferenceRepository.loadUsers();
    users && actions.app.appData.fetchedUsers(users);

    const departments = await appReferenceRepository.loadDepartments();
    departments && actions.app.appData.fetchedDepartments(departments);

    const categories = await appReferenceRepository.loadIssueCategories();
    categories && actions.issue.fetchedIssueCategories(categories);

    const sorting = await appReferenceRepository.loadIssueSortingTypes();
    sorting && actions.issue.fetchedIssueSortingTypes(sorting);

    const labels = await appReferenceRepository.loadLabels();
    labels && actions.issue.fetchedLabels(labels);
  }

  private async runOnlineBackgroundSync(
    actions: ReturnType<typeof store.getActions>
  ): Promise<void> {
    actions.app.offline.setSyncingOfflineData(true);
    try {
      const wrap = (name: string, job: Promise<unknown>) =>
        job.catch((err) => {
          console.warn(`[AppDataLoader] ${name} sync failed:`, err);
        });

      await Promise.all([
        wrap('fetchUnreadNotificationsCount', actions.notification.fetchUnreadNotificationsCount()),
        wrap('fetchUnreadChatCount', actions.chat.fetchUnreadNotificationsCount()),
        wrap('fetchFavoriteMachines', actions.machines.fetchFavorites()),
        wrap('fetchLastUsedMachines', actions.machines.fetchLastUsed()),
        wrap('fetchFavoriteProjects', actions.projects.fetchFavorites()),
        wrap('fetchLastUsedProjects', actions.projects.fetchLastUsed()),
        wrap('fetchFavoriteIssues', issueRepository.fetchAndCacheFavoriteIssues('all')),
        wrap('fetchFavoriteServices', serviceRepository.fetchAndCacheFavoriteServices('all')),
        wrap('fetchFavoriteSpecifications', documentationRepository.fetchAndCacheFavoriteSpecifications()),
      ]);
    } finally {
      actions.app.offline.setSyncingOfflineData(false);
    }
  }
}

export const appDataLoader = new AppDataLoader();
